import React from 'react';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { NamedLink } from '../../components';
import { useLocation } from 'react-router-dom';
import {
  ACCOUNT_SETTINGS_PAGES,
  DASHBOARD_PAGES,
  SCHEDULE_PAGES,
  VENDOR_PAGES,
  VENDOR_ACTIVITY_PAGES,
} from '../../routing/routeConfiguration';

import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarSeparator,
} from '../ui/sidebar';

import {
  LayoutDashboard,
  Users,
  Calendar,
  CalendarClock,
  CalendarDays,
  List,
  UserCog,
  Mail,
  Lock,
  CreditCard,
  Wallet,
  Settings,
  Activity,
} from 'lucide-react';

interface AppShellNavProps {
  currentPage: string;
  userType?: string;
  showManageListingsLink?: boolean;
  showPaymentMethods?: boolean;
  showPayoutDetails?: boolean;
}

const linkClass = 'no-underline hover:no-underline text-sidebar-foreground text-base';
const iconClass = 'h-5 w-5';

const AppShellNav: React.FC<AppShellNavProps> = ({
  currentPage,
  userType,
  showManageListingsLink = false,
  showPaymentMethods = false,
  showPayoutDetails = false,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const isVendor = userType === 'vendor' || userType === 'provider';

  const isActive = (page: string) => {
    if (page === 'ManageListingsPage') return currentPage === 'ManageListingsPage';
    if (page === 'ProfileSettingsPage') return currentPage === 'ProfileSettingsPage';
    if (page === 'SchedulePage') return SCHEDULE_PAGES.includes(currentPage);
    if (page === 'DashboardBasePage') {
      return DASHBOARD_PAGES.includes(currentPage) && currentPage !== 'DashboardPage';
    }
    if (page === 'DashboardPage') return currentPage === 'DashboardPage';
    return currentPage === page;
  };

  const isDashboardActive = DASHBOARD_PAGES.includes(currentPage);

  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupLabel>
          <FormattedMessage id="AppShell.mainGroup" />
        </SidebarGroupLabel>
        <SidebarMenu>
          {isVendor ? (
            /* ── Vendor nav ── */
            <>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={
                    VENDOR_PAGES.includes(currentPage) &&
                    !VENDOR_ACTIVITY_PAGES.includes(currentPage) &&
                    currentPage !== 'VendorSessionsPage' &&
                    !location.pathname.startsWith('/dashboard/activities') &&
                    !location.pathname.startsWith('/dashboard/sessions')
                  }
                  tooltip={intl.formatMessage({ id: 'AppShell.vendorOverview' })}
                >
                  <NamedLink name="VendorOverviewPage" className={linkClass}>
                    <LayoutDashboard className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.vendorOverview" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={
                    VENDOR_ACTIVITY_PAGES.includes(currentPage) ||
                    location.pathname.startsWith('/dashboard/activities')
                  }
                  tooltip={intl.formatMessage({ id: 'AppShell.vendorActivities' })}
                >
                  <NamedLink name="VendorActivitiesPage" className={linkClass}>
                    <Activity className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.vendorActivities" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={
                    currentPage === 'VendorSessionsPage' ||
                    location.pathname.startsWith('/dashboard/sessions')
                  }
                  tooltip={intl.formatMessage({ id: 'AppShell.vendorSessions' })}
                >
                  <NamedLink name="VendorSessionsPage" className={linkClass}>
                    <CalendarDays className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.vendorSessions" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          ) : (
            /* ── Parent/consumer nav ── */
            <>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isDashboardActive} tooltip={intl.formatMessage({ id: 'AppShell.dashboard' })}>
                  <NamedLink name="DashboardBasePage" className={linkClass}>
                    <LayoutDashboard className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.dashboard" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isDashboardActive && currentPage === 'DashboardPage'} tooltip={intl.formatMessage({ id: 'AppShell.children' })}>
                  <NamedLink name="DashboardPage" params={{ tab: 'children' }} className={linkClass}>
                    <Users className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.children" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={isDashboardActive && currentPage === 'DashboardPage'} tooltip={intl.formatMessage({ id: 'AppShell.calendar' })}>
                  <NamedLink name="DashboardPage" params={{ tab: 'calendar' }} className={linkClass}>
                    <Calendar className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.calendar" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={SCHEDULE_PAGES.includes(currentPage)} tooltip={intl.formatMessage({ id: 'AppShell.schedule' })}>
                  <NamedLink name="SchedulePage" className={linkClass}>
                    <CalendarClock className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.schedule" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          )}
        </SidebarMenu>
      </SidebarGroup>

      {/* Listings group — consumers only; vendors manage activities via VendorDashboardPage */}
      {showManageListingsLink && !isVendor && (
        <>
          <SidebarSeparator />
          <SidebarGroup>
            <SidebarGroupLabel>
              <FormattedMessage id="AppShell.listingsGroup" />
            </SidebarGroupLabel>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive('ManageListingsPage')}
                  tooltip={intl.formatMessage({ id: 'AppShell.yourListings' })}
                >
                  <NamedLink name="ManageListingsPage" className={linkClass}>
                    <List className={iconClass} />
                    <span>
                      <FormattedMessage id="AppShell.yourListings" />
                    </span>
                  </NamedLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </>
      )}

      {/* Settings group */}
      <SidebarSeparator />
      <SidebarGroup>
        <SidebarGroupLabel>
          <FormattedMessage id="AppShell.settingsGroup" />
        </SidebarGroupLabel>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive('ProfileSettingsPage')}
              tooltip={intl.formatMessage({ id: 'AppShell.profile' })}
            >
              <NamedLink name="ProfileSettingsPage" className={linkClass}>
                <UserCog className={iconClass} />
                <span>
                  <FormattedMessage id="AppShell.profile" />
                </span>
              </NamedLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive('ContactDetailsPage')}
              tooltip={intl.formatMessage({ id: 'AppShell.contactDetails' })}
            >
              <NamedLink name="ContactDetailsPage" className={linkClass}>
                <Mail className={iconClass} />
                <span>
                  <FormattedMessage id="AppShell.contactDetails" />
                </span>
              </NamedLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive('PasswordChangePage')}
              tooltip={intl.formatMessage({ id: 'AppShell.password' })}
            >
              <NamedLink name="PasswordChangePage" className={linkClass}>
                <Lock className={iconClass} />
                <span>
                  <FormattedMessage id="AppShell.password" />
                </span>
              </NamedLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
          {showPayoutDetails && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive('StripePayoutPage')}
                tooltip={intl.formatMessage({ id: 'AppShell.payments' })}
              >
                <NamedLink name="StripePayoutPage" className={linkClass}>
                  <CreditCard className={iconClass} />
                  <span>
                    <FormattedMessage id="AppShell.payments" />
                  </span>
                </NamedLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          {showPaymentMethods && (
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                isActive={isActive('PaymentMethodsPage')}
                tooltip={intl.formatMessage({ id: 'AppShell.paymentMethods' })}
              >
                <NamedLink name="PaymentMethodsPage" className={linkClass}>
                  <Wallet className={iconClass} />
                  <span>
                    <FormattedMessage id="AppShell.paymentMethods" />
                  </span>
                </NamedLink>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              isActive={isActive('ManageAccountPage')}
              tooltip={intl.formatMessage({ id: 'AppShell.manageAccount' })}
            >
              <NamedLink name="ManageAccountPage" className={linkClass}>
                <Settings className={iconClass} />
                <span>
                  <FormattedMessage id="AppShell.manageAccount" />
                </span>
              </NamedLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
  );
};

export default AppShellNav;
